import { useState, useEffect, useRef } from 'react';
import { Loader2, Save, Check, ImageIcon, Trash2, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useRemoveMyAvatar, useUpdateProfile, useUploadMyAvatar } from '@/hooks/use-profile';
import { toast } from '@/components/ui/toaster';
import { getInitials, cn } from '@/lib/utils';
import type { User } from '@/types';

const STANDARD_AVATARS = [
  '/avatars/milovanderlinden-servicedesk-employee.svg',
  '/avatars/msewtz-Business-Person.svg',
  '/avatars/nicubunu_Comic_characters_Bearded_guy.svg',
  '/avatars/nicubunu_Comic_characters_Blonde.svg',
  '/avatars/nicubunu_Comic_characters_Pirate.svg',
  '/avatars/chovynz-OCAL-Userpic-RobinHood.svg',
  '/avatars/nicubunu_Game_baddie_Nurse.svg',
  '/avatars/nicubunu_Game_baddie_Santa.svg',
  '/avatars/HappyPandaFace.svg',
  '/avatars/Little-funny-penguin.svg',
  '/avatars/Bee-6.svg',
  '/avatars/funny-frog.svg',
  '/avatars/funny-girl.svg',
  '/avatars/boy4.svg',
  '/avatars/brown-funny-monkey.svg',
  '/avatars/cochon.svg',
];

interface ProfileFormProps {
  user: User;
}

const ALLOWED_AVATAR_MIME_TYPES = new Set([
  'image/svg+xml',
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
]);

export function ProfileForm({ user }: ProfileFormProps) {
  const updateProfile = useUpdateProfile();
  const uploadMyAvatar = useUploadMyAvatar();
  const removeMyAvatar = useRemoveMyAvatar();

  const [displayName, setDisplayName] = useState(user.displayName);
  const [avatarUrl, setAvatarUrl] = useState(user.avatarUrl || '');
  const [localAvatarPreviewUrl, setLocalAvatarPreviewUrl] = useState<string | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showAvatarPicker, setShowAvatarPicker] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isBusy = updateProfile.isPending || uploadMyAvatar.isPending || removeMyAvatar.isPending;

  useEffect(() => {
    setDisplayName(user.displayName);
    setAvatarUrl(user.avatarUrl || '');
    setLocalAvatarPreviewUrl(null);
    setIsDirty(false);
  }, [user]);

  useEffect(() => {
    const hasChanges =
      displayName !== user.displayName || avatarUrl !== (user.avatarUrl || '');
    setIsDirty(hasChanges);
  }, [displayName, avatarUrl, user]);

  useEffect(() => {
    return () => {
      if (localAvatarPreviewUrl) {
        URL.revokeObjectURL(localAvatarPreviewUrl);
      }
    };
  }, [localAvatarPreviewUrl]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!displayName.trim()) {
      toast({ title: 'Display name is required', variant: 'destructive' });
      return;
    }

    if (!isDirty) return;

    try {
      await updateProfile.mutateAsync({
        displayName: displayName.trim(),
        avatarUrl: avatarUrl.trim() || undefined,
      });
      toast({ title: 'Profile updated successfully' });
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 2000);
    } catch {
      toast({ title: 'Failed to update profile', variant: 'destructive' });
    }
  };

  const handleAvatarFileSelected = async (file: File | null) => {
    if (!file) return;

    if (!ALLOWED_AVATAR_MIME_TYPES.has(file.type)) {
      toast({
        title: 'Unsupported avatar format',
        variant: 'destructive',
      });
      return;
    }

    if (localAvatarPreviewUrl) {
      URL.revokeObjectURL(localAvatarPreviewUrl);
    }
    const preview = URL.createObjectURL(file);
    setLocalAvatarPreviewUrl(preview);

    try {
      const updatedUser = await uploadMyAvatar.mutateAsync(file);
      setAvatarUrl(updatedUser.avatarUrl || '');
      setShowAvatarPicker(false);
      toast({ title: 'Avatar uploaded successfully' });
    } catch {
      setLocalAvatarPreviewUrl(null);
      toast({ title: 'Failed to upload avatar', variant: 'destructive' });
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveAvatar = async () => {
    const hasUnsavedAvatarSelection = avatarUrl !== (user.avatarUrl || '');
    if (hasUnsavedAvatarSelection) {
      setAvatarUrl('');
      setLocalAvatarPreviewUrl(null);
      return;
    }

    if (!avatarUrl) return;

    try {
      const updatedUser = await removeMyAvatar.mutateAsync();
      setAvatarUrl(updatedUser.avatarUrl || '');
      setLocalAvatarPreviewUrl(null);
      toast({ title: 'Avatar removed' });
    } catch {
      toast({ title: 'Failed to remove avatar', variant: 'destructive' });
    }
  };

  const previewAvatar = localAvatarPreviewUrl || avatarUrl.trim() || undefined;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Profile Information</CardTitle>
        <CardDescription>Update your personal information</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="displayName">Display Name</Label>
            <Input
              id="displayName"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Your display name"
              disabled={updateProfile.isPending}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input id="email" value={user.email} disabled />
            <p className="text-xs text-muted-foreground">
              Contact an administrator to change your email address
            </p>
          </div>

          <div className="space-y-2">
            <Label>Avatar</Label>
            <div className="flex items-center gap-4">
              <Avatar className="h-16 w-16">
                <AvatarImage src={previewAvatar} />
                <AvatarFallback className="text-xl">
                  {getInitials(displayName || user.displayName)}
                </AvatarFallback>
              </Avatar>
              <div className="flex gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".svg,.jpg,.jpeg,.png,.gif,.webp,image/svg+xml,image/jpeg,image/png,image/gif,image/webp"
                  className="hidden"
                  onChange={(e) => {
                    const nextFile = e.target.files?.[0] ?? null;
                    void handleAvatarFileSelected(nextFile);
                  }}
                  disabled={isBusy}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isBusy}
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Upload Avatar
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowAvatarPicker(!showAvatarPicker)}
                  disabled={isBusy}
                >
                  <ImageIcon className="h-4 w-4 mr-2" />
                  Choose Avatar
                </Button>
                {avatarUrl && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => void handleRemoveAvatar()}
                    className="text-destructive"
                    disabled={isBusy}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Remove
                  </Button>
                )}
              </div>
            </div>

            {showAvatarPicker && (
              <div className="grid grid-cols-4 sm:grid-cols-8 gap-2 p-3 border rounded-lg mt-2">
                {STANDARD_AVATARS.map((url) => (
                  <button
                    key={url}
                    type="button"
                    onClick={() => {
                      setAvatarUrl(url);
                      setLocalAvatarPreviewUrl(null);
                      setShowAvatarPicker(false);
                    }}
                    className={cn(
                      'p-2 rounded-lg hover:bg-muted transition-colors flex items-center justify-center',
                      avatarUrl === url && 'ring-2 ring-primary'
                    )}
                  >
                    <img
                      src={url}
                      alt="Avatar option"
                      className="h-10 w-10 rounded-full object-cover"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  </button>
                ))}
              </div>
            )}

          </div>

          <div className="flex items-center gap-3">
            <Button
              type="submit"
              disabled={isBusy || !isDirty}
              className="gap-2"
            >
              {updateProfile.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : showSuccess ? (
                <>
                  <Check className="h-4 w-4" />
                  Saved
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  Save Changes
                </>
              )}
            </Button>

            {isDirty && !isBusy && (
              <p className="text-sm text-muted-foreground">You have unsaved changes</p>
            )}
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
